import React, { useEffect, useState } from "react";
import { supabase } from "./lib/supabaseClient";
import RedirectHandler from "./pages/RedirectHandler";
import LandingPage from "./pages/LandingPage";
import usePlan from "./hooks/usePlan";
import UpgradeModal from "./components/UpgradeModal";
import AdminDashboard from "./pages/AdminDashboard";
import PublicPage from "./pages/PublicPages";
import NotificationsSettings from "./pages/NotificationsSettings";
import TeamMembers from "./pages/TeamMembers";
import CouponManager from "./pages/CouponManager";
import HelpCenter from "./pages/HelpCenter";
import DynamicQRIndia from "./pages/seo/DynamicQRIndia";
import RestaurantMenuQR from "./pages/seo/RestaurantMenuQR";
import GoogleReviewQR from "./pages/seo/GoogleReviewQR";
import WiFiQR from "./pages/seo/WiFiQR";
import LeadCaptureQR from "./pages/seo/LeadCaptureQR";

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";

import AdvancedGenerator from "./AdvancedGenerator";
import BulkGenerator from "./pages/BulkGenerator";
import QRDetails from "./QRDetails";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import Layout from "./Layout";
import Auth from "./Auth";
import LegalPages from "./pages/LegalPages";
import ResetPassword from "./pages/ResetPassword";
import FeedbackInbox from "./pages/FeedbackInbox";
import LeadsInbox from "./pages/LeadsInbox";
import CardView from "./pages/CardView";
import MyQRCodes from "./MyQRCodes";
import MenuBuilder from "./pages/MenuBuilder";
import QRHealthMonitor from "./pages/QRHealthMonitor";
import MenuPage from "./pages/MenuPage";
import UpiQRPage from "./pages/seo/UpiQR";

// ERROR BOUNDARY
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("UI CRASH:", error.message);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-black text-white flex-col gap-4 p-10">
          <p className="text-red-500 font-black text-xl">Error</p>
          <p className="text-yellow-400 text-sm font-mono">{this.state.error?.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// PROTECTED ROUTE — waits for both session AND admin check before rendering
function ProtectedRoute({ user, sessionReady, adminReady, children }) {
  if (!sessionReady || !adminReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

// PLAN ROUTE — shows spinner while plan loads, never shows paywall during loading
function PlanRoute({ plan, required, user, loading, children }) {
  const [showUpgrade, setShowUpgrade] = useState(false);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617]">
      <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const allowed = required === "agency"
    ? plan === "agency" || plan === "trial"
    : plan === "pro" || plan === "agency" || plan === "trial";

  if (!allowed) return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-[#020617] text-white flex-col gap-4">
        <h2 className="text-xl font-black uppercase">
          {required === "agency" ? "Agency" : "Pro"} Feature
        </h2>
        <p className="text-sm text-gray-400">Upgrade your plan to access this</p>
        <button
          onClick={() => setShowUpgrade(true)}
          className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-3 rounded-xl font-black uppercase text-sm"
        >
          Upgrade Now
        </button>
      </div>
      {showUpgrade && (
        <UpgradeModal
          user={user}
          onClose={() => setShowUpgrade(false)}
          onSuccess={() => { }}
        />
      )}
    </>
  );
  return children;
}

// QR DETAILS WRAPPER
function QRDetailsWrapper() {
  const { id } = useParams();
  const [qr, setQr] = useState(null);

  useEffect(() => {
    async function fetchQR() {
      const { data, error } = await supabase
        .from("qr_codes")
        .select("*")
        .eq("id", id)
        .single();
      if (error) { console.error("QR LOAD FAILED:", error); return; }
      setQr(data);
    }
    fetchQR();
  }, [id]);

  if (!qr) return <div className="text-white p-20 text-center">Loading QR...</div>;
  return <QRDetails qr={qr} />;
}

// MAIN APP
export default function App() {
  const [user, setUser] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminReady, setAdminReady] = useState(false);

  const { plan, limits, trialDaysLeft, isOnTrial, loading: planLoading } = usePlan(user?.id, isAdmin);

  const checkAdmin = async (userId) => {
    if (!userId) {
      setIsAdmin(false);
      setAdminReady(true);
      return;
    }
    // Hardcoded admin ID — always gets agency plan instantly
    if (userId === "39354205-3d56-466b-8aae-d90a069e5f77") {
      setIsAdmin(true);
      setAdminReady(true);
      return;
    }
    try {
      const { data } = await supabase
        .from("admins")
        .select("user_id")
        .eq("user_id", userId)
        .maybeSingle();
      setIsAdmin(!!data);
    } catch {
      setIsAdmin(false);
    } finally {
      setAdminReady(true);
    }
  };

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!active) return;
      const u = session?.user || null;
      setUser(u);
      setSessionReady(true);
      checkAdmin(u?.id);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      const u = session?.user || null;
      setUser(u);
      setSessionReady(true);
      checkAdmin(u?.id);
    });

    return () => {
      active = false;
      listener?.subscription.unsubscribe();
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* PUBLIC ROUTES */}
          <Route path="/go/:code" element={<RedirectHandler />} />
          <Route path="/p/:page" element={<PublicPage />} />
          <Route path="/menu/:code" element={<MenuPage />} />
          <Route path="/card/:code" element={<CardView />} />
          <Route path="/privacy" element={<LegalPages />} />
          <Route path="/terms" element={<LegalPages />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/upi-qr-code-generator" element={<UpiQRPage />} />
          <Route path="/dynamic-qr-code-generator-india" element={<DynamicQRIndia />} />
          <Route path="/restaurant-menu-qr-code" element={<RestaurantMenuQR />} />
          <Route path="/google-review-qr-code" element={<GoogleReviewQR />} />
          <Route path="/wifi-qr-code-generator" element={<WiFiQR />} />
          <Route path="/lead-capture-qr-code" element={<LeadCaptureQR />} />

          {/* ADMIN — self-contained auth */}
          <Route path="/admin" element={<AdminDashboard />} />

          {/* LANDING PAGE */}
          <Route
            path="/"
            element={
              !sessionReady
                ? <div className="min-h-screen bg-[#020617] flex items-center justify-center text-white">
                  <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
                </div>
                : user
                  ? <Navigate to="/dashboard" replace />
                  : <LandingPage />
            }
          />

          {/* LOGIN */}
          <Route
            path="/login"
            element={
              !sessionReady
                ? <div className="min-h-screen bg-[#020617] flex items-center justify-center text-white">
                  <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
                </div>
                : user
                  ? <Navigate to="/dashboard" replace />
                  : <Auth />
            }
          />

          {/* DASHBOARD */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute user={user} sessionReady={sessionReady} adminReady={adminReady}>
                <Layout user={user} isAdmin={isAdmin} isOnTrial={isOnTrial} trialDaysLeft={trialDaysLeft} />
              </ProtectedRoute>
            }
          >
            <Route index element={<MyQRCodes user={user} />} />

            <Route path="create" element={
              <AdvancedGenerator key="create" user={user} plan={plan} limits={limits} />
            } />

            <Route path="bulk" element={
              <PlanRoute plan={plan} required="agency" user={user} loading={planLoading}>
                <BulkGenerator user={user} onBulkComplete={() => window.location.href = "/dashboard"} />
              </PlanRoute>
            } />

            <Route path="qr/:id" element={<QRDetailsWrapper />} />

            <Route path="edit/:id" element={
              <AdvancedGenerator user={user} plan={plan} limits={limits} />
            } />

            <Route path="leads" element={<LeadsInbox user={user} />} />
            <Route path="feedback" element={<FeedbackInbox user={user} />} />

            <Route path="analytics" element={
              <PlanRoute plan={plan} required="pro" user={user} loading={planLoading}>
                <AnalyticsDashboard user={user} />
              </PlanRoute>
            } />

            <Route path="menu-builder" element={<MenuBuilder user={user} />} />
            <Route path="health" element={<QRHealthMonitor user={user} />} />
            <Route path="team" element={<TeamMembers user={user} plan={plan} />} />
            <Route path="coupons" element={<CouponManager user={user} />} />
            <Route path="notifications" element={<NotificationsSettings user={user} />} />
            <Route path="settings" element={<NotificationsSettings user={user} />} />
            <Route path="help" element={<HelpCenter />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
